import { ApolloCache, Cache } from '@apollo/client';
import { produce } from 'immer';

/**
 * Helper function for updating apollo query cache after a mutation
 *
 * @param store apollo cache store
 * @param queryOptions apollo query to update
 * @param updateFn an immer draft function that lets you modify the query in place
 */
export function updateQueryCache<QueryType, TVariables, MutationT = any>(
  store: ApolloCache<MutationT>,
  queryOptions: Cache.ReadQueryOptions<QueryType, TVariables>,
  updateFn: (draft: QueryType) => void
) {
  try {
    const cache = store.readQuery<QueryType, TVariables>(queryOptions);
    if (!cache) {
      return;
    }
    // Make updates
    const data = produce(cache, updateFn);
    store.writeQuery<QueryType, TVariables>({
      ...queryOptions,
      data,
    });
  } catch (err) {
    console.error(err);
  }
}

/**
 * Helper function for updating apollo fragment cache after a mutation
 *
 * @param store apollo cache store
 * @param queryOptions apollo fragment to update
 * @param updateFn an immer draft function that lets you modify the query in place
 */
export function updateFragmentCache<
  FragmentType,
  TVariables = unknown,
  MutationT = any,
>(
  store: ApolloCache<MutationT>,
  fragmentOptions: Cache.ReadFragmentOptions<FragmentType, TVariables>,
  updateFn: (draft: FragmentType) => void
) {
  try {
    const cache = store.readFragment<FragmentType, TVariables>(fragmentOptions);
    if (!cache) {
      return;
    }
    // Make updates
    const data = produce(cache, updateFn);
    store.writeFragment<FragmentType, TVariables>({
      ...fragmentOptions,
      data,
    });
  } catch (err) {
    console.error(err);
  }
}
